import { Divider, Flex, SectionMessage, Spacing } from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { CustomerModal } from '@features/contacts/elements/CustomerModal'
import { CustomerModalSubmitActionType } from '@features/contacts/enums/customerModalSubmitActionType'
import { Contact } from '@features/contacts/types/contact'
import { ImportedInvoice } from '@features/importedInvoices/types/importedInvoice'
import { getImportedInvoiceReviewFormSchema } from '@features/importedInvoices/utils/getImportedInvoiceReviewFormSchema'
import { isImportedInvoiceApprovable } from '@features/importedInvoices/utils/isImportedInvoiceApprovable'
import { TaxRate } from '@modules-deprecated/app/taxrates/types'

import { ModalId } from '../../../../../../../../enums/modalId'
import { useForm } from '../../../../../../../../hooks/useForm'
import { useImportedInvoiceReviewApprove } from '../../hooks/useImportedInvoiceReviewApprove'
import { useImportedInvoiceReviewClientCreation } from '../../hooks/useImportedInvoiceReviewClientCreation'
import { getImportedInvoiceReviewFormDefaultValues } from '../../utils/getImportedInvoiceReviewFormDefaultValues'
import { ImportedInvoiceApproveButton } from '../ImportedInvoiceApproveButton'
import { ImportedInvoiceReviewGeneralFields } from '../ImportedInvoiceReviewGeneralFields'
import { ImportedInvoiceReviewLines } from '../ImportedInvoiceReviewLines'
import { ImportedInvoiceReviewTotals } from '../ImportedInvoiceReviewTotals'
import * as Styled from './styles'

interface ImportedInvoiceReviewFormContentProps {
  baseCurrencyId: string
  clientContact?: Contact
  invoice: ImportedInvoice
  isClientAmbiguous: boolean
  isClientToCreate: boolean
  taxRates: TaxRate[] | undefined
}

/**
 * The sales-invoice review pane: the general fields, the invoice lines, and the totals footer.
 * A row to review is pre-filled from the agentic extraction and approved from the drawer header.
 *
 * Purchase category and Receipt date from the Figma are purchase-only and deliberately absent.
 */
export const ImportedInvoiceReviewFormContent = ({
  baseCurrencyId,
  clientContact,
  invoice,
  isClientAmbiguous,
  isClientToCreate,
  taxRates,
}: ImportedInvoiceReviewFormContentProps): ReactElement => {
  const { t } = useTranslation()
  const { extraction } = invoice
  // `useForm` reads the defaults once on mount, so this only has to stay stable, not fresh.
  const defaultValues = useMemo(
    () =>
      getImportedInvoiceReviewFormDefaultValues({
        baseCurrencyId,
        clientContact,
        extraction,
        isClientAmbiguous,
        taxRates,
      }),
    [baseCurrencyId, clientContact, extraction, isClientAmbiguous, taxRates],
  )
  const validationSchema = useMemo(() => getImportedInvoiceReviewFormSchema(t), [t])
  const { clearErrors, Form, formState, getValues, handleSubmit, setValue } = useForm({
    defaultValues,
    validationSchema,
  })
  const { customerType, handleInvalidSubmit, initialValues } = useImportedInvoiceReviewClientCreation({
    clearErrors,
    client: extraction.header?.client,
    getValues,
    isClientToCreate,
  })
  const { approveErrorMessage, approveReview, isApproving } = useImportedInvoiceReviewApprove({
    handleSubmit,
    isSubmitting: formState.isSubmitting,
    onInvalidSubmit: handleInvalidSubmit,
  })

  const isReadOnly = !isImportedInvoiceApprovable(invoice)

  const handleCustomerModalSubmit = useCallback(
    (contact: Contact) => {
      setValue('client', contact)
      approveReview()
    },
    [approveReview, setValue],
  )

  return (
    <Styled.ImportedInvoiceReviewFormWrapper>
      <Form name="imported-invoice-review">
        <Flex flexDirection="column" gap={Spacing.S32}>
          {!isReadOnly && (
            <SectionMessage icon="sparkle" variant="info">
              {t('imported_invoices.review.prefilled_information')}
            </SectionMessage>
          )}
          {approveErrorMessage && <SectionMessage variant="error">{approveErrorMessage}</SectionMessage>}
          <ImportedInvoiceReviewGeneralFields isClientToCreate={isClientToCreate} isReadOnly={isReadOnly} />
          <ImportedInvoiceReviewLines isReadOnly={isReadOnly} />
          <Divider />
          <ImportedInvoiceReviewTotals />
        </Flex>
        {!isReadOnly && <ImportedInvoiceApproveButton isApproving={isApproving} onClick={approveReview} />}
      </Form>
      {/* Kept outside the review form, which would otherwise wrap the customer form's own fields. */}
      <CustomerModal
        defaultCustomerType={customerType}
        id={ModalId.CustomerModal}
        initialValues={initialValues}
        onSubmit={handleCustomerModalSubmit}
        submitActionType={CustomerModalSubmitActionType.Create}
      />
    </Styled.ImportedInvoiceReviewFormWrapper>
  )
}
